import { useEffect, useMemo, useRef, useState } from "react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useSnackbar } from "notistack";
import dayjs from "@lib/dayjs";
import { useSelector } from "react-redux";
import PaperSendIcon from "@assets/svg/PaperSendIcon";
import useChat from "@custom-hooks/useChat";
import { RootState } from "@store/reducers";
import MessageBubble from "@pages/MessagesPage/components/MessageBubble";
import PaperClipIcon from "@assets/svg/PaperClipIcon";
import { EnumUserTypes } from "@utils/constants";
import { identity } from "@utils/mergeMessages";

const MessagesTab = ({ applicationDetails }) => {
  const { enqueueSnackbar } = useSnackbar();

  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCurrentlyTypingRef = useRef(false);

  const user = useSelector((state:RootState)=>state.auth.user);

  const receiverDetails = useMemo(
    () => ({
      ...applicationDetails?.candidateDetails,
      userId: applicationDetails?.candidateDetails?.userId,
      userType:EnumUserTypes.CANDIDATE,
    }),
    [applicationDetails?.candidateDetails, applicationDetails?.candidateDetails]
  );

  console.log('OG candidate details', applicationDetails?.candidateDetails?.userId)

  const {
    messages,
    isMessageLoading,
    sendMessage,
    loadOlderMessages,
    hasNextPage,
    isOlderMessageLoading,
    triggerTyping,
    isTyping
  } = useChat({
    jobId: applicationDetails?.jobId,
    receiverDetails,
  });
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleScroll = () => {
    const el = messagesContainerRef.current;
    if (!el || isOlderMessageLoading) return;
    const isAtTop = el.scrollTop === 0;
    if (isAtTop && hasNextPage) {
      loadOlderMessages();
    }
  };

  const scrollToBottom = (smooth = false) => {
    if (messagesContainerRef?.current) {
      messagesContainerRef?.current.scrollTo({
        top: messagesContainerRef?.current?.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendClick();
    } else {
      if (!isCurrentlyTypingRef.current) {
        triggerTyping(true);
        isCurrentlyTypingRef.current = true;
      }

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        triggerTyping(false);
        isCurrentlyTypingRef.current = false;
      }, 2000);
    }
  };

  const handleSendClick = () => {
    if (isLoading || !message.trim()) return;
    sendMessage({
      message,
      setMessage,
      attachments,
      setAttachments,
    });

    triggerTyping(false);
    isCurrentlyTypingRef.current = false;
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
  };

  useEffect(() => {
    if (messages?.length) {
      scrollToBottom();
    }
  }, [messages]);

  console.log('applicationDetails', applicationDetails);


  if (isMessageLoading) {
    return (
      <div className="flex justify-center mt-40">
        <LoadingAnimation color="black" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-[70%] self-center relative px-5 pt-2 pb-8 min-h-0">
      <div className="bg-[#fafafa] rounded-md flex flex-col flex-1 min-h-0">
        <div
          ref={messagesContainerRef}
          onScroll={handleScroll}
          className="flex flex-col gap-5 flex-grow overflow-y-auto no-scrollbar p-6 min-h-0 h-[69vh]"
        >
          {isOlderMessageLoading ? (
            <div className="flex items-center justify-center font-medium text-xs gap-3">
              Loading older messages <LoadingAnimation color="#2e2e2e" />
            </div>
          ) : null}
          {messages?.length ? (
            messages?.map((item) => {
              return (
                <div
                  key={identity(item)}
                  className="flex flex-col gap-2"
                >
                  {/* {item?._id} {unseenMessageFrom?._id} */}
                  {/* {item?._id === unseenMessageFrom?._id ? (
                  <NewMessageDivider />
                ) : null} */}
                  <MessageBubble item={item} receiverDetails={receiverDetails} user={user}/>
                </div>
              );
            })
          ) : (
            <p className="text-sm text-center flex h-[67vh] items-center justify-center pb-10">
              No messages available.
            </p>
          )}
        </div>

        {/* Typing and message Input area */}
        <div className="flex flex-col px-1.5 pb-1.5 pt-0">
          <div className="h-6 flex items-center px-2">
            {isTyping && (
              <span className="text-xs text-gray-500 italic">
                Typing...
              </span>
            )}
          </div>
          <div className="flex gap-4 rounded-md border p-1.5 bg-white">
            <textarea
              className="px-3 py-2 w-full bg-white rounded text-xs font-medium focus:border-gray-300 focus:outline-none"
              rows={1}
              placeholder="Type your message here..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
            ></textarea>
            <button
              className="text-xs text-charcoal font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md disabled:cursor-not-allowed"
              onClick={handleSendClick}
              disabled={isLoading || !message.trim()}
            >
              {isLoading ? (
                <LoadingAnimation color="black" className={"w-4 h-4"} />
              ) : (
                <PaperSendIcon />
              )}
            </button>
            <button
              className="text-xs text-charcoal font-medium bg-white px-1 hover:bg-gray-200 flex items-center size-7 justify-center rounded-md"
            // disabled={isLoading || !message.trim() || !selectedContact}
            >
              <PaperClipIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesTab;
